import React, { useEffect, useState, useCallback, useRef } from "react";
import moment from "moment";
import { useTranslation } from 'react-i18next';
import { useStoreState, useStoreActions } from 'easy-peasy';

import { useAuthContext } from "contexts/AuthContext"

import usePermissions from "hooks/usePermissions";
import WriteButton from "components/Buttons/WriteButton/WriteButton";
import ReadOnlyBanner from "components/ReadOnlyBanner/ReadOnlyBanner";
import SinglePortfolioViewSelect from "components/SinglePortfolioViewSelect/SinglePortfolioViewSelect"
import { useTargetPortfolio } from "components/TargetPortfolioDialog/useTargetPortfolio";
// Storybook
import { Row, Col } from "stories/layout";
import { Input, DatePicker } from "stories/forms";
import { Card, Typography, Button, Link, IconButton, Modal, Spinner } from "stories/components";
import { PageWrapper, PageHeader } from "components/Page"
import { TableAuto } from "stories/tables"



// App
import OptionEdit from "views/app/OptionEdit/OptionEdit"
import { getDate } from "services/datetime"
import { useAppContext } from "contexts/AppContext"
import { round, thousandFormat, formatDerivativePrice, DERIVATIVE_PRICE_DECIMALS } from "services/helpers"
import ValueColor from "components/ValueColor"
import OptionAssetTypeInput from "components/Forms/OptionAssetTypeInput/OptionAssetTypeInput"
import OptionSecuritySelect from "components/Forms/OptionSecuritySelect/OptionSecuritySelect"
import OptionStateInput from "components/Forms/OptionStateInput/OptionStateInput"
import { getOptionStateLabel } from "components/Forms/OptionStateInput/optionStateLabels"
import SubscriptionOptionsInfo from "components/Info/SubscriptionOptionsInfo.js"
import { httpGetOptionsExcel } from "services/options"
import UpdateUp from "views/vismapay/UpdateUp/UpdateUp"
import ImportExcelOptions from 'views/app/ImportExcel/ImportExcelOptions'

import { getListOptionsForDisplay, getListRowHistoryContracts, hasOptionHistory } from "views/app/OptionsList/optionHistoryUtils"
import { bindScrollSync, getTopScrollbarState } from "views/app/OptionsList/scrollSync"
import { FROZEN_COLUMN_COUNT, applyStickyColumnOffsets } from "views/app/OptionsList/stickyColumns"
import { openDerivativeOutcome } from "views/app/OptionsList/openDerivativeOutcome"
import "./OptionsList.css"

const rights = [
  {key: 2, value: "Call option"},
  {key: 3, value: "Put option"},
]

const defaultFilters = {
  offset: 0,
  limit: 100,
  ordering: "-id",
  asset_type: "",
  option_security: "",
  option_state: "",
  trade_id: "",
  start_date_from: undefined,
  start_date_to: undefined,
  end_date_from: undefined,
  end_date_to: undefined,
}

var date = new Date();

const OptionsList = ({match, history}) => {
  
  const { t } = useTranslation();
  const { enums, getOptionsAssetType, getDerivativeType } = useAppContext();
  const account = useStoreState((state) => state.account?.account);
  const account_loading = useStoreState((state) => state.account?.loading);

  const {accountUserType} = useAuthContext()
  

  const { canWrite } = usePermissions();
  const portfolio = useStoreState((state) => state.portfolio?.portfolio);
  // With several active portfolios a new derivative or import asks for its portfolio first, and
  // an existing derivative is edited in its own portfolio (#1409).
  const { choose, forRow } = useTargetPortfolio();
  
  const [rows, setRows] = useState()
  const tableWrapperRef = useRef(null)
  const topScrollRef = useRef(null)
  const [topScroll, setTopScroll] = useState({ spacerWidth: 0, visible: false })
  const [statistics, setStatistics] = useState({})
  const [loadingExport, setLoadingExport] = useState(false)
  
  const loading = useStoreState((state) => state.options?.loading);
  const options = useStoreState((state) => state.options?.options);
  const getOptions = useStoreActions((actions) => actions.options.getOptions);
  const setOptions = useStoreActions((actions) => actions.options.setOptions);
  const count = useStoreState((state) => state.options?.count);
  const setCount = useStoreActions((actions) => actions.options.setCount);
  
  const [headers, setHeaders] = useState([
    { label: t("options_list_id", "ID" ), key: "id", sortable: true, sortingKey: "id"},
    { label: t("options_list_actions", " " ), key: "actions"},
    { label: t("options_list_trade_id", "Trade id" ), key: "trade_id", sortable: true, sortingKey: "trade_id"},
    { label: t("options_list_state", "State" ), key: "state", sortable: true, sortingKey: "option_state"},
    { label: t("options_list_derivative_type", "Derivative type" ), key: "derivative_type", sortable: true, sortingKey: "derivative_type"},
    { label: t("options_list_asset_type", "Asset type" ), key: "asset_type", visible: false, sortable: true, sortingKey: "asset_type"},
    { label: t("options_list_asset", "Asset" ), key: "asset", sortable: true, sortingKey: "asset_name"},
    // { label: t("options_list_right", "Right" ), key: "right", sortable: true, sortingKey: "option_right"},
    { label: t("options_list_owner", "Owner" ), key: "owner", sortable: true, sortingKey: "owner_name"},
    { label: t("options_list_terminated", "Terminoitu" ), key: "terminated"},
    { label: t("options_list_writer", "Writer" ), key: "writer", sortable: true, sortingKey: "writer_name"},
    { label: t("options_list_lot_size", "Lot size" ), key: "lot_size", sortable: true, sortingKey: "lot_size"},
    { label: t("options_list_lot_amount", "Lots amount" ), key: "lot_amount", sortable: true, sortingKey: "lot_amount"},
    { label: t("options_list_quantity", "Total quantity" ), key: "quantity", sortable: true, sortingKey: "quantity"},
    { label: t("options_list_start_date", "Start date" ), key: "start_date", sortable: true, sortingKey: "start_date"},
    { label: t("options_list_end_date", "End date" ), key: "end_date", sortable: true, sortingKey: "end_date"},
    { label: t("options_list_premium", "Premium/ Share" ), key: "premium", sortable: true, sortingKey: "premium"},
    { label: t("options_list_premium_total", "Premium total" ), key: "premium_total", sortable: true, sortingKey: "premium_total"},
    { label: t("options_list_other_expenses", "Other exp." ), key: "other_expenses", sortable: true, sortingKey: "other_expenses"},
    { label: t("options_list_strike_price", "Strike price" ), key: "strike_price", sortable: true, sortingKey: "strike_price"},
    { label: t("options_list_spot_price", "Spot price" ), key: "spot_price"},
    { label: t("options_list_current_outcome", "Current outcome" ), key: "current_outcome", visible: false },
    { label: t("options_list_option_state_date", "State date" ), key: "option_state_date", sortable: true, sortingKey: "option_state_date"},
    { label: t("options_list_exercise_price", "Exercise price" ), key: "exercise_price", sortable: true, sortingKey: "exercise_price"},
    { label: t("options_list_termination_price", "Termination price" ), key: "termination_fee", sortable: true, sortingKey: "termination_fee"},
    { label: t("options_list_exercise_outcome", "Exercise outcome" ), key: "exercise_outcome", sortable: true, sortingKey: "exercise_outcome"},
    { label: t("options_list_notes", "Notes" ), key: "notes"},
  ]);
  
  // BEGIN
  
  const [currentDate, setCurrentDate] = useState(date.setDate(date.getDate() - 1)); // default: yesterday
  const [expandedHistoryRows, setExpandedHistoryRows] = useState({})
  const historyToggleAt = useRef(0)
  const acceptHistoryToggle = () => {
    const now = Date.now()
    if (now - historyToggleAt.current < 50) return false
    historyToggleAt.current = now
    return true
  }
  const [modalEditOptionOpen, setModalEditOptionOpen] = useState(false)
  const [modalEditOptionId, setModalEditOptionId] = useState()

  const openModalEditOption = (id) => {
    if (id != null) {
      const option = (options || []).find((o) => String(o?.id) === String(id))
      if (option?.portfolio) {
        forRow(option.portfolio)
      }
    }
    setModalEditOptionOpen(true)
    setModalEditOptionId(id)
  }

  const toggleHistoryRow = (optionId) => {
    if (!acceptHistoryToggle()) return
    setExpandedHistoryRows((prev) => ({
      ...prev,
      [optionId]: !prev[optionId],
    }))
  }

  const historyRowIds = (rows || [])
    .filter((row) => hasOptionHistory(row, options || []))
    .map((row) => row?.id)
    .filter((id) => id != null)

  const allHistoriesOpen = historyRowIds.length > 0
    && historyRowIds.every((id) => expandedHistoryRows[id])

  const toggleAllHistories = () => {
    if (!acceptHistoryToggle()) return
    if (allHistoriesOpen) {
      setExpandedHistoryRows({})
      return
    }
    const next = {}
    historyRowIds.forEach((id) => {
      next[id] = true
    })
    setExpandedHistoryRows(next)
  }

  const historyToggleButton = (label, onClick, align = "end") => (
    <div className={`d-flex w-100 justify-content-${align}`}>
      <Button
        className="btn-sm"
        color="secondary"
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
          onClick();
        }}
      >
        {label}
      </Button>
    </div>
  )

  const tableHeaders = headers.map((header) => {
    if (header.key !== "terminated") return header
    return {
      ...header,
      label: historyRowIds.length > 0
        ? historyToggleButton(
            allHistoriesOpen
              ? t("options_list_hide_all_history", "Sulje kaikki")
              : t("options_list_show_all_history", "Avaa kaikki"),
            toggleAllHistories,
            "center"
          )
        : null,
    }
  })

  const handleDateChange = event => {
    setCurrentDate(event.target.value)
  }
  
  const handleChange = ({target}) => {
    setFilters({
      ...filters,
      [target.name]: target.value,
    })
  }
  const setFieldValue = (name, value) => {
    setFilters(s => ({
      ...s,
      [name]: value
    }))
  }
  
  const getOption = (id) => {
    return options && options.reduce((result, item) => {
      if (item.id === id) { result = item }
      return result
    }, null)
  }
  
  const getSpotPrice = (id) => {
    const _option = getOption(id)
    return _option?.spot_price ? _option?.spot_price : 0
  }
  
  const getCurrentOutcome = (id) => {
    return openDerivativeOutcome(getOption(id), getSpotPrice(id))
  }
  
  const getRightValue = (key) => {
    if (!key) {
      return ""
    }
    const obj = rights && rights.filter((item) => {
        return item?.key == key
      })[0]
    if (obj) {
      return t(obj?.value)
    }
    return "";
  }
  
  const getStateValue = (key) => {
    if (key == null || key === "") {
      return ""
    }
    const translated = getOptionStateLabel(t, key);
    if (translated) {
      return translated;
    }
    const option_states = enums?.options?.option?.option_state;
    const obj = option_states && option_states.filter((item) => {
        return item?.key == key
      })[0]
    if (obj) {
      return t(obj?.value, obj?.value)
    }
    return "";
  }

  const getOptionStateDisplay = (stateKey) => {
    const label = getStateValue(stateKey) || "-";

    switch (Number(stateKey)) {
      case 2:
        return { label, icon: "fa-circle", colorClass: "text-primary" };
      case 3:
        return { label, icon: "fa-trash-alt", colorClass: "text-danger" };
      case 4:
        return { label, icon: "fa-hourglass-end", colorClass: "text-warning" };
      case 5:
        return { label, icon: "fa-ban", colorClass: "text-secondary" };
      case 6:
        return { label, icon: "fa-trash-alt", colorClass: "text-danger" };
      default:
        return { label, icon: "fa-question-circle", colorClass: "text-muted" };
    }
  };

  const renderOptionStateCell = (stateKey) => {
    const { label, icon, colorClass } = getOptionStateDisplay(stateKey);

    return (
      <span className="options-list-state d-inline-flex align-items-center">
        <i className={`fas ${icon} ${colorClass} options-list-state__icon mr-2`} aria-hidden />
        <span>{label}</span>
      </span>
    );
  };
  
  // END

  // Update subscription BEGIN

    const openUpdateUp = useCallback(() => {
      return (<>{account?.license?.limit_feature_options && <UpdateUp redirectToFrontpage={true}/>}</>)
    }, [account?.license])
  // Update subscription END

  // Table
  const [filters, setFilters] = useState({
    ...defaultFilters,
    initialSearchDone: false,
  })
  
  const handleClearFilters = () => {
    setFilters((prev) => ({
      ...defaultFilters,
      portfolio: prev?.portfolio,
      stock_date: prev?.stock_date,
      initialSearchDone: prev?.initialSearchDone,
    }))
  }
  
  const handleGetOptions = () => {
    const params = {
      ...filters,
      start_date_from: filters?.start_date_from ? moment(filters?.start_date_from).format("YYYY-MM-DD") : undefined,
      start_date_to: filters?.start_date_to ? moment(filters?.start_date_to).format("YYYY-MM-DD") : undefined,
      end_date_from: filters?.end_date_from ? moment(filters?.end_date_from).format("YYYY-MM-DD") : undefined,
      end_date_to: filters?.end_date_to ? moment(filters?.end_date_to).format("YYYY-MM-DD") : undefined,
      stock_date: filters?.stock_date ? moment(filters?.stock_date).format("YYYY-MM-DD") : undefined,
      asset_name: filters?.option_security,
    }
    if (params?.portfolio && params?.stock_date) {
      getOptions(params)
    }
  }
  
  const handleSubmit = () => {
    handleGetOptions()
  }
  
  
  useEffect(() => {
    if (filters?.initialSearchDone === false && filters?.portfolio && filters?.portfolio > 0 && filters?.stock_date) {
      handleGetOptions()
      setFieldValue("initialSearchDone", true)
    }
  }, [filters])
  
  
  
  
  useEffect(() => {
    const portfolioId = portfolio?.id
    if (filters?.portfolio == null || String(filters.portfolio) === String(portfolioId ?? "")) {
      // First portfolio (the initial search fetches it) or the same one: nothing to reset.
      setFilters(s => ({ ...s, portfolio: portfolioId }))
      return
    }
    // "Näytettävä salkku" changed: drop the previous portfolio's rows, paging, opened
    // histories and target asset (its options come from that portfolio), and search again
    // from the new one (#1409).
    setOptions([])
    setExpandedHistoryRows({})
    setFilters(s => ({
      ...s,
      portfolio: portfolioId,
      offset: 0,
      option_security: "",
      initialSearchDone: false,
    }))
  }, [portfolio?.id])
  
  useEffect(() => {
    setFilters(s => ({
      ...s,
      stock_date: currentDate
    }))
    
  }, [currentDate])

  const paginationPrevious = () => {
    let offset = filters?.offset - filters?.limit;
    if (offset < 0) {
      offset = 0;
    }
    setFilters(f => ({
      ...f,
      offset: offset,
    }))
  }
  
  const paginationNext = () => {
    let offset = filters?.offset + filters?.limit;
    if (offset > filters?.count) {
      return
    }
    setFilters(f => ({
      ...f,
      offset: offset,
    }))
  }

  useEffect(() => {
    const wrapper = tableWrapperRef.current
    const top = topScrollRef.current
    const bottom = wrapper?.querySelector(".table-responsive")
    if (!bottom || !top) return undefined

    const update = () => {
      setTopScroll(getTopScrollbarState(bottom))
      applyStickyColumnOffsets(bottom, FROZEN_COLUMN_COUNT)
    }
    update()
    const unbind = bindScrollSync(bottom, top)
    let observer
    const measured = bottom.querySelector("table") || bottom
    if (typeof ResizeObserver !== "undefined") {
      observer = new ResizeObserver(update)
      observer.observe(measured)
      const headerCells = bottom.querySelectorAll("thead tr:first-child th")
      for (let index = 0; index < FROZEN_COLUMN_COUNT && index < headerCells.length; index += 1) {
        observer.observe(headerCells[index])
      }
    }
    window.addEventListener("resize", update)
    return () => {
      unbind()
      if (observer) observer.disconnect()
      window.removeEventListener("resize", update)
    }
  }, [rows, loading, headers])
  
  useEffect(() => {
    let _statistics = {
      current_outcome: 0,
      current_open_outcome: 0,
      completed_outcome: 0,
      rows_count: 0,
      rows_open: 0,
      rows_open_pct: 0,
      rows_exercised: 0,
      rows_exercised_pct: 0,
      rows_expired: 0,
      rows_expired_pct: 0,
      rows_terminated: 0,
      rows_terminated_pct: 0,
      completed_count: 0, // Exercised or Expired
      completed_won: 0,
      completed_won_pct: 0,
      completed_lost: 0,
      completed_lost_pct: 0,
    }
    
    setRows(
      getListOptionsForDisplay(options).map((row) => {
        const optionId = row?.id;
        const current_outcome = round(getCurrentOutcome(optionId), 4)
        _statistics.current_outcome += current_outcome
        const exercise_outcome = row?.exercise_outcome ? row?.exercise_outcome : 0.0
        _statistics.completed_outcome += parseFloat(exercise_outcome);
        
        _statistics.rows_count += 1
        if (row?.option_state === 2) {
          _statistics.rows_open += 1
          _statistics.current_open_outcome += current_outcome
        }
        else if (row?.option_state === 3) {
          _statistics.rows_exercised += 1
        }
        else if (row?.option_state === 4) {
          _statistics.rows_expired += 1
        }
        else if (row?.option_state === 5) {
          _statistics.rows_terminated += 1
        }
        
        if (row?.option_state === 3 || row?.option_state === 4 || row?.option_state === 5) {
          _statistics.completed_count += 1
        
          if (exercise_outcome >= 0) {
            _statistics.completed_won += 1
          }
          else if (exercise_outcome < 0) {
            _statistics.completed_lost += 1
          }
        }
        
        return {
          ...row,
          current_outcome: round(current_outcome, 2),
        }
      })
    )
    
    _statistics.rows_open_pct = round((_statistics.rows_open / _statistics.rows_count * 100.0), 2) 
    _statistics.rows_exercised_pct = round((_statistics.rows_exercised / _statistics.rows_count * 100.0), 2) 
    _statistics.rows_expired_pct = round((_statistics.rows_expired / _statistics.rows_count * 100.0), 2)
    _statistics.rows_terminated_pct = round((_statistics.rows_terminated / _statistics.rows_count * 100.0), 2)
    
    _statistics.completed_won_pct = round((_statistics.completed_won / _statistics.completed_count * 100.0), 2) 
    _statistics.completed_lost_pct = round((_statistics.completed_lost / _statistics.completed_count * 100.0), 2) 
    
    _statistics.current_open_outcome = round(_statistics.current_open_outcome, 2)
    _statistics.current_outcome = round(_statistics.current_outcome, 2)
    _statistics.completed_outcome = round(_statistics.completed_outcome, 2)
    
    setStatistics(_statistics)
  }, [options, currentDate])
  
  const formatNumber = (value, decimals = 2) => {
    const parsed = Number(value);
    if (!Number.isFinite(parsed)) {
      return thousandFormat(0);
    }
    return thousandFormat(round(parsed, decimals).toFixed(decimals));
  };

  const buildOptionTableRow = (row, extras = {}) => {
    const {
      isHistoryRow = false,
      historyStep = null,
      historyClassName = "",
    } = extras;
    const optionId = row?.id;
    const optionAssetType = getOptionsAssetType(row?.asset_type);
    const derivativeType = getDerivativeType(row?.derivative_type);
    const spotPrice = getSpotPrice(optionId);
    const strikePrice = row?.strike_price ? formatNumber(row?.strike_price, DERIVATIVE_PRICE_DECIMALS) : formatDerivativePrice(0);
    const assetName = row?.asset ? row?.asset?.name : row?.asset_name;

    return {
      ...row,
      rowKey: isHistoryRow
        ? `history-${extras.parentRowId}-${optionId}-${historyStep}`
        : optionId,
      isHistoryRow,
      historyOptionId: isHistoryRow ? optionId : undefined,
      className: historyClassName || undefined,
      id: isHistoryRow
        ? {
            content: (
              <span className="options-history-id">
                <span className="options-history-step" aria-hidden>
                  {historyStep}
                </span>
                <span className="options-history-id__value">{optionId}</span>
              </span>
            ),
          }
        : optionId,
      derivative_type: derivativeType ? derivativeType?.value : "-",
      right: getRightValue(row?.option_right),
      asset: {
        content: (
          <span className="font-weight-bold">
            {assetName || "-"}
          </span>
        ),
      },
      asset_type: optionAssetType ? optionAssetType?.value : "-",
      owner: row?.owner_name,
      terminated: isHistoryRow ? (
        <span className="text-muted">-</span>
      ) : hasOptionHistory(row, options || []) ? (
        historyToggleButton(
          expandedHistoryRows[optionId]
            ? t("options_list_hide_history", "Piilota historia")
            : t("options_list_show_history", "Näytä historia"),
          () => toggleHistoryRow(optionId)
        )
      ) : (
        <span className="text-muted">-</span>
      ),
      writer: row?.writer_name,
      lot_size: {
        content: formatNumber(row?.lot_size, 0),
        className: "text-right",
      },
      lot_amount: {
        content: formatNumber(row?.lot_amount, 0),
        className: "text-right",
      },
      quantity: {
        content: formatNumber(row?.quantity, 0),
        className: "text-right",
      },
      start_date: row?.start_date ? getDate(row?.start_date) : "-",
      end_date: row?.end_date ? getDate(row?.end_date) : "-",
      premium: {
        content: formatNumber(row?.premium, DERIVATIVE_PRICE_DECIMALS),
        className: "text-right",
      },
      premium_total: {
        content: formatNumber(row?.premium_total, DERIVATIVE_PRICE_DECIMALS),
        className: "text-right",
      },
      other_expenses: {
        content: formatNumber(row?.other_expenses, DERIVATIVE_PRICE_DECIMALS),
        className: "text-right",
      },
      termination_fee: {
        content: formatNumber(row?.termination_fee, 2),
        className: "text-right",
      },
      strike_price: {
        content: strikePrice,
        className: "text-right",
      },
      spot_price: {
        content: spotPrice ? formatNumber(spotPrice, 4) : "0.0000",
        className: "text-right",
      },
      current_outcome: <ValueColor value={row?.current_outcome} />,
      state: renderOptionStateCell(row?.option_state),
      option_state_date: row?.option_state_date ? getDate(row?.option_state_date) : "-",
      exercise_date: row?.exercise_date ? getDate(row?.exercise_date) : "-",
      exercise_price: row?.option_state > 2 ? (row?.exercise_price ? formatNumber(row?.exercise_price, DERIVATIVE_PRICE_DECIMALS) : "-") : "",
      exercise_outcome: {
        content: <ValueColor value={row?.exercise_outcome} />,
        className: "text-right",
      },
      notes: row?.notes && row?.notes.length > 0 ? t("sk_yes", "Yes") : t("sk_yes", "No"),
      actions: isHistoryRow ? (
        <Button
          className="btn-sm"
          color="secondary"
          onClick={(event) => {
            event.stopPropagation();
            openModalEditOption(optionId);
          }}
        >
          {t("options_history_open", "Avaa")}
        </Button>
      ) : (
        <Button
          className="btn-sm"
          color="secondary"
          onClick={() => openModalEditOption(optionId)}
        >
          {t("edit_button", "Edit")}
        </Button>
      ),
    };
  };

  const getTableRows = () => {
    const tableRows = [];

    rows && rows.forEach((row) => {
      const optionId = row?.id;
      tableRows.push(buildOptionTableRow(row));

      if (expandedHistoryRows[optionId]) {
        const historyContracts = getListRowHistoryContracts(row, options || []);
        historyContracts.forEach((contract, index) => {
          const isFirst = index === 0;
          const isLast = index === historyContracts.length - 1;
          tableRows.push(
            buildOptionTableRow(contract, {
              isHistoryRow: true,
              parentRowId: optionId,
              historyStep: historyContracts.length - index,
              historyClassName: [
                "options-history-row",
                isFirst ? "options-history-row--first" : "",
                isLast ? "options-history-row--last" : "",
              ]
                .filter(Boolean)
                .join(" "),
            })
          );
        });
      }
    });

    return tableRows;
  };

  // Modal: subscription options info
    
  const [modalEditOpenSubscriptionOptions, setModalEditOpenSubscriptionOptions] = useState(true)
  const [modalEditIdSubscriptionOptions, setModalEditIdSubscriptionOptions] = useState()
  
  const toggleModalEditSubscriptionOptions = () => {
      setModalEditOpenSubscriptionOptions(s => !s);
  }
  
  const openModalEditSubscriptionOptions = (id) => {
      setModalEditOpenSubscriptionOptions(true)
      setModalEditIdSubscriptionOptions(id);
  }
  
  const modalEditResolveSubscriptionOptions = () => {
      setModalEditOpenSubscriptionOptions(false);
      setModalEditIdSubscriptionOptions(null);
  }
  
  const modalEditRejectSubscriptionOptions = () => {
      setModalEditOpenSubscriptionOptions(false);
      setModalEditIdSubscriptionOptions(null);
  }
  
  // Modal: New/Edit option
  
  const toggleModalEditOption = () => {
    setModalEditOptionOpen(s => !s);
  }
  
  const modalEditOptionResolve = () => {
    setModalEditOptionOpen(false);
    setModalEditOptionId(null);
    handleGetOptions()
  }
  
  const modalEditOptionReject = () => {
    setModalEditOptionOpen(false);
    setModalEditOptionId(null);
  }



  // Modal: import

  const [modalEditImportOpen, setModalEditImportOpen] = useState(false)
  const [modalEditImportId, setModalEditImportNordeaId] = useState()
  
  const toggleModalEditImport = () => {
    setModalEditImportOpen(s => !s);
  }
  
  const openModalEditImport = (id, related_model_type, ticker_symbol) => {
    setModalEditImportOpen(true)
    setModalEditImportNordeaId(id);
  }
  
  const modalEditImportResolve = () => {
    setModalEditImportOpen(false);
    setModalEditImportNordeaId(null);
    handleGetOptions()
  }
  
  const modalEditImportReject = () => {
    setModalEditImportOpen(false);
    setModalEditImportNordeaId(null);
  
  }
  
  const handleExcelExport = () => {
    
    const params = {
      ...filters,
      start_date_from: filters?.start_date_from ? moment(filters?.start_date_from).format("YYYY-MM-DD") : undefined,
      start_date_to: filters?.start_date_to ? moment(filters?.start_date_to).format("YYYY-MM-DD") : undefined,
      end_date_from: filters?.end_date_from ? moment(filters?.end_date_from).format("YYYY-MM-DD") : undefined,
      end_date_to: filters?.end_date_to ? moment(filters?.end_date_to).format("YYYY-MM-DD") : undefined,
      stock_date: filters?.stock_date ? moment(filters?.stock_date).format("YYYY-MM-DD") : undefined,
      ordering: '-id',
      client_timezone_offset: Intl.DateTimeFormat().resolvedOptions().timeZone,
    }
    
    let fileName = "investime_derivatives_export.xlsx";
    let a = document.createElement("a");
    document.body.appendChild(a);
    a.style = "display: none";
    
    setLoadingExport(true)
    httpGetOptionsExcel(params).then(response => {
      let file = new Blob([response.data], {type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'});
      let fileURL = window.URL.createObjectURL(file);
      a.href = fileURL;
      a.download = fileName;
      a.click();
    })
    .finally(() => {
      setLoadingExport(false)
    })
  }

  if (account_loading) {
    return(
      <div className="m-5"></div>
    )
  }

  const handleEmailClick = () => {
      window.location.href = 'mailto:support@investime.fi';
  };
  
  
  if (account?.license?.limit_feature_options && false) {
    
    return(
      <>
      <div className="d-flex justify-content-between">
        <div className="p-3">
    
          <Typography bold variant="h3" className="text-dark mt-3 mb-5">{t("Toiminto ei ole käytössä tilauksellanne.")}</Typography>
    
        <Typography variant="small" className="text-dark mt-3 mb-1">{t("Muuttaaksesi tilausta ota yhteyttä tukipalveluumme.")}</Typography>
        <Button variant="secondary" >
            <Link href="https://www.investime.fi/ota-yhteytta/" target="_blank" className="text-dark">{t("subscription_options_info_change_subcription","Vaihda tilausta")}</Link>
        </Button>

        </div>
      </div>
      <Modal
        title={t("Derivative")}
        isOpen={modalEditOpenSubscriptionOptions}
        toggleModal={toggleModalEditSubscriptionOptions}
        size="modal-xl"
        showHeader={false}
        classNameBody="p-0 padding-0"
      >
        <SubscriptionOptionsInfo
          objectId={modalEditIdSubscriptionOptions}
          handleResolve={modalEditResolveSubscriptionOptions}
          handleReject={modalEditRejectSubscriptionOptions}
          custom_info_text= {t("subscription_options_info_text", "Tämä toiminto ei ole käytössä tilauksellanne")}
        />
      </Modal>
    </>
    )
    
    /*
    return(
      <>
        <div className="d-flex justify-content-between">
          <div className="p-3">
       
            <Typography bold variant="h3" className="text-dark mt-3 mb-5">{t("Toiminto ei ole käytössä tilauksellanne.")}</Typography>
      
          <Typography variant="small" className="text-dark mt-3 mb-1">{t("Muuttaaksesi tilausta ota yhteyttä tukipalveluumme.")}</Typography>
          <Typography bold variant="h4" className="text-dark mt-2 mb-1">
            <a href="mailto:support@investime.fi" onClick={handleEmailClick}>
              {t("support@investime.fi")}
            </a>
          </Typography>

          </div>
        </div>
      </>          
    )
    */
   
  }



  return (
    <>
      <ReadOnlyBanner className="mb-3" />
      {openUpdateUp()}

      <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-2">
        <WriteButton
          className="sk-quick-actions__btn"
          variant="secondary"
          size="sm"
          onClick={async () => { if (await choose("option")) openModalEditOption(null) }}
        >
          <i className="fas fa-plus sk-quick-actions__icon" aria-hidden />
          <span>{t("new_option_button", "New derivative")}</span>
        </WriteButton>
      </div>
      
      <SinglePortfolioViewSelect id="options-list-portfolio" className="mb-2" />
      <div className="d-flex flex-wrap align-items-end mb-3 sk-filter-toolbar">

          <div className="d-flex flex-column py-2 pr-2 mr-2 options-list-filter-item" style={{minWidth: '100px'}}>
            <Typography bold={false} className="sk-field-label pr-1 text-nowrap">
              {t("options_list_spot_date", "Kurssipäivä")}
            </Typography>
            <DatePicker
              className="form-control form-control-sm options-list-date-input"
              selected={currentDate}
                name="stock_date"
                onChange={(date) => setCurrentDate(date)}
                showYearDropdown
                dateFormat="d.M.yyyy"
              />
          </div>

          <div className="d-flex flex-column py-2 pr-2 mr-2 options-list-filter-item" style={{minWidth: '130px'}}>
            <OptionAssetTypeInput
              name="asset_type"
              onChange={handleChange}
              label={t("options_list_asset_type", "Asset type")}
              allOption={true}
              value={filters?.asset_type}
            />
          </div>

          <div className="d-flex flex-column py-2 pr-2 mr-2 options-list-filter-item" style={{minWidth: '140px'}}>
            {/* The select reads only asset_name: no stock_date, which filters nothing and only made the server price every contract. */}
            <OptionSecuritySelect
              name="option_security"
              onChange={handleChange}
              label={t("options_list_security", "Kohde etuus")}
              allOption={true}
              options={options}
              asset_type= {filters?.asset_type}
              payload={{portfolio: filters?.portfolio, asset_type: filters?.asset_type }}
              value={filters?.option_security}
            />
          </div>

          <div className="d-flex flex-column py-2 pr-2 mr-2 options-list-filter-item" style={{minWidth: '100px'}}>
            <OptionStateInput
              name="option_state"
              onChange={handleChange}
              label={t("option_edit_state", "State")}
              allOption={true}
              value={filters?.option_state}
            />
          </div>
          
          <div className="d-flex flex-column py-2 pr-2 mr-2 options-list-filter-item" style={{minWidth: '100px'}}>
            <Input
              name="trade_id"
              label={t("options_list_trade_id", "Trade id")}
              value={filters?.trade_id}
              onChange={handleChange} 
            />
          </div>
            
          <div className="d-flex flex-column py-2 pr-2 mr-2 options-list-filter-item" style={{minWidth: '100px'}}>
            <Typography bold={false} className="sk-field-label pr-1 text-nowrap">
              {t("options_list_start_date_from", "Aloit. alkaen")}
            </Typography>
            <DatePicker
              className="form-control form-control-sm options-list-date-input"
              selected={filters?.start_date_from}
                name="start_date_from"
                onChange={(date) => setFieldValue("start_date_from", date)}
                showYearDropdown
                dateFormat="d.M.yyyy"
              />
          </div>
          <div className="d-flex flex-column py-2 pr-2 mr-2 options-list-filter-item" style={{minWidth: '100px'}}>
            <Typography bold={false} className="sk-field-label pr-1 text-nowrap">
              {t("options_list_start_date_to", "Aloit. päättyen")}
            </Typography>
            <DatePicker
              className="form-control form-control-sm options-list-date-input"
              selected={filters?.start_date_to}
                name="start_date_to"
                onChange={(date) => setFieldValue("start_date_to", date)}
                showYearDropdown
                dateFormat="d.M.yyyy"
              />
          </div>
          
          <div className="d-flex flex-column py-2 pr-2 mr-2 options-list-filter-item" style={{minWidth: '100px'}}>
            <Typography bold={false} className="sk-field-label pr-1 text-nowrap">
              {t("options_list_end_date_from", "Päät. alkaen")}
            </Typography>
            <DatePicker
              className="form-control form-control-sm options-list-date-input"
              selected={filters?.end_date_from}
                name="end_date_from"
                onChange={(date) => setFieldValue("end_date_from", date)}
                showYearDropdown
                dateFormat="d.M.yyyy"
              />
          </div>
          <div className="d-flex flex-column py-2 pr-2 mr-2 options-list-filter-item" style={{minWidth: '100px'}}>
            <Typography bold={false} className="sk-field-label pr-1 text-nowrap">
              {t("options_list_end_date_to", "Päät. päättyen")}
            </Typography>
            <DatePicker
              className="form-control form-control-sm options-list-date-input"
              selected={filters?.end_date_to}
                name="end_date_to"
                onChange={(date) => setFieldValue("end_date_to", date)}
                showYearDropdown
                dateFormat="d.M.yyyy"
              />
          </div>

          <div className="d-flex align-items-end py-2 ml-2 sk-filter-toolbar__search">
            <Button variant="secondary" className="mr-2 sk-quick-actions__btn sk-filter-search-btn" onClick={handleSubmit}>{t("web_search_button","Hae")}</Button>
            {(filters?.asset_type || filters?.option_security || filters?.option_state || filters?.trade_id || filters?.start_date_from || filters?.start_date_to || filters?.end_date_from || filters?.end_date_to) && (
              <Button variant="link" size="sm" onClick={handleClearFilters}>{t("web_clear_filters_button","Tyhjennä suodattimet")}</Button>
            )}
          </div>
          
          {/*
          <div className="p-2">
            <Input label={t("options_list_start_date_from", "Start date (from)")} name="start_date_from" type="date" value={filters?.start_date_from} onChange={handleChange} />
          </div>
          <div className="p-2 pr-4">
            <Input label={t("options_list_start_date_to", "Start date (to)")} name="start_date_to" type="date" value={filters?.start_date_to} onChange={handleChange} />
          </div>
          
          <div className="p-2">
            <Input label={t("options_list_end_date_from", "End date (from)")} name="end_date_from" type="date" value={filters?.end_date_from} onChange={handleChange} />
          </div>
          <div className="p-2 pr-4">
            <Input label={t("options_list_end_date_to", "End date (to)")} name="end_date_to" type="date" value={filters?.end_date_to} onChange={handleChange} />
          </div>
          */}
          

          
      </div>
      
      <div className="options-list-table-wrapper" ref={tableWrapperRef}>
      <div
        className={`options-list-top-scroll${topScroll.visible ? "" : " options-list-top-scroll--hidden"}`}
        ref={topScrollRef}
        aria-hidden="true"
      >
        <div className="options-list-top-scroll__spacer" style={{ width: topScroll.spacerWidth }} />
      </div>
      <TableAuto
        className="options-list-table-spaced"
        showRowNumber={false}
        showId={false}
        checkboxes={false}
        headers={tableHeaders}
        headerColor={"light"}
        rows={getTableRows()}
        loading={loading}
        pagination={true}
        paginationPrevious={paginationPrevious}
        paginationNext={paginationNext}
        paginationOffset={filters?.offset}
        paginationLimit={filters?.limit}
        paginationCount={count}
        filters={filters}
        setFilters={setFilters}
        customOnClick={(id, _type, _symbol, _typeOriginal, row) => {
          const optionId = row?.historyOptionId != null ? row.historyOptionId : id;
          if (optionId != null && typeof optionId !== "object") {
            openModalEditOption(optionId);
          }
        }}
      />
      </div>
      
      <div className="sk-table-footer-actions d-flex flex-row-reverse flex-wrap align-items-center py-4">
        <Button color="secondary" size="sm" disabled={loadingExport} onClick={handleExcelExport}>
          <i className="fas fa-file-excel"></i>&nbsp;
          {t("excel_export", "Vie (Excel)")}
        </Button>
        <Button className="btn-sm mr-2" color="secondary" onClick={async () => { if (await choose("option")) openModalEditImport(null) }}>
          <i className="fas fa-file-excel"></i>&nbsp;
          {t("web_import_excel", "Tuo Excel")}
        </Button>
      </div>
      
      <>
        <Row>
          <Col>{t("web_summary","Summary")}</Col>
        </Row>
        
        { loading ? (
          <Spinner />
        ) : (
          <Row>
            <Col className="col-sm-12 col-md-7">
              <Typography variant="small">
                <Row>
                  <Col>{t("web_derivatives_open","Derivatives open")}</Col>
                  <Col>{statistics?.rows_open} ({statistics?.rows_open_pct}%)</Col>
                </Row>
                
                <Row>
                  <Col>{t("web_derivatives_exercised","Derivatives exercised")}</Col>
                  <Col>{statistics?.rows_exercised} ({statistics?.rows_exercised_pct}%)</Col>
                </Row>
                
                <Row>
                  <Col>{t("web_derivatives_expired","Derivatives expired")}</Col>
                  <Col>{statistics?.rows_expired} ({statistics?.rows_expired_pct}%)</Col>
                </Row>
                
                <Row>
                  <Col>{t("web_derivatives_terminated","Derivatives terminated")}</Col>
                  <Col>{statistics?.rows_terminated} ({statistics?.rows_terminated_pct}%)</Col>
                </Row>
                
                <Row>
                  <Col>{t("web_derivative_rows_total","Derivative rows total")}</Col>
                  <Col>{statistics?.rows_count} (100%)</Col>
                </Row>
                
                {/*
                <Row>
                  <Col>{t("web_current_outcome_all","Current outcome (all)")}</Col>
                  <Col><ValueColor value={statistics?.current_outcome} /></Col>
                </Row>
                
                <Row>
                  <Col>{t("web_current_outcome_open","Current outcome (open)")}</Col>
                  <Col><ValueColor value={statistics?.current_open_outcome} /></Col>
                </Row>
                */}
                
                <Row>
                  <Col>{t("web_completed_outcome","Completed outcome (exercised, expired and terminated)")}</Col>
                  <Col><ValueColor value={statistics?.completed_outcome} /></Col>
                </Row>
                
                <Row>
                  <Col>{t("web_completed_won","Completed won")}</Col>
                  <Col>{statistics?.completed_won} ({statistics?.completed_won_pct}%)</Col>
                </Row>
                
                <Row>
                  <Col>{t("web_completed_lost","Completed lost")}</Col>
                  <Col>{statistics?.completed_lost} ({statistics?.completed_lost_pct}%)</Col>
                </Row>
              </Typography>
            </Col>
          </Row>
        )}
      </>

      
      <Modal
        title={
          modalEditOptionId
            ? t("web_derivative_title", "Derivative")
            : `${t("new_option_button", "New derivative")}${portfolio?.name ? `: ${portfolio.name}` : ""}`
        }
        isOpen={modalEditOptionOpen}
        toggleModal={toggleModalEditOption}
        closeModal={modalEditOptionReject}
        size="modal-xl"
        classNameBody="p-0 padding-0"
      >
        <OptionEdit
          objectId={modalEditOptionId}
          handleResolve={modalEditOptionResolve}
          handleReject={modalEditOptionReject}
        />
      </Modal>

      <Modal
        title={t("Excel tuonti")}
        showHeader={true}
        isOpen={modalEditImportOpen}
        // isOpen={true}
        toggleModal={toggleModalEditImport}
        closeModal={modalEditImportReject}
        size="modal-xl"
      >
        {/* <ImportTransactionsNordnet
          objectId={modalEditImportId}
          handleResolve={modalEditImportNordeaResolve}
          handleReject={modalEditImportNordeaReject}
         
          defaultStockMarket={filters?.stock_market}
          defaultInvestmentType={investmentType}
          setFilters={setFilters}
        /> */}
        <ImportExcelOptions
          objectId={modalEditImportId}
          handleResolve={modalEditImportResolve}
          handleReject={modalEditImportReject}
         
          defaultStockMarket={filters?.stock_market}
          // defaultInvestmentType={investmentType}
          setFilters={setFilters}
        />
      </Modal>
      
    </>
  );
}

export default OptionsList;

